import _ from 'lodash';
import connect from 'connect-alt';
import React, { Component, PropTypes } from 'react';
import { Modal } from 'react-bootstrap';
import { getWeatherCategoryFromCondition } from '../../utils/WeatherUtils';
import { formatWsiDate, formatDate, convertHoursToSeconds, getDateTimeInSeconds } from '../../utils/DateUtils';
import { addAlertToStore, findAlertInStore, removeAlertFromStore } from '../../utils/AlertUtils';
import Timestamp from '../Timestamp';
import ComponentTitle from '../shared/ComponentTitle';
import WeatherAlertIcon from './WeatherAlertIcon';
import CancelIcon from 'components/icons/Cancel';
import { TITLE_TYPE } from '../../constants/FRNBaseStyles';
import DedicatedWeatherAlert from './DedicatedWeatherAlert';

const ALERT_CATEGORIES = ['warning', 'watch', 'advisory', 'statement', 'outlook', 'synopsis', 'forecast', 'alert'];
const WEATHER_ALERT_TYPE = {
  banner: 'banner',
  fullView: 'fullView'
};

class WeatherAlertWrapper extends Component {
  static propTypes = {
    type: PropTypes.oneOf(Object.keys(WEATHER_ALERT_TYPE)),
    FRN_resourceEndpoints: PropTypes.array
  }

  static defaultProps = {
    type: WEATHER_ALERT_TYPE.banner,
    FRN_resourceEndpoints: []
  }

  render() {
    const {
      FRN_resourceEndpoints: [{
        url = ''
      } = {}], 
      type
    } = this.props;

    if (!url) {
      return <WeatherAlerts {...this.props}/>;
    }

    if (type === WEATHER_ALERT_TYPE.fullView) {
      return <DedicatedWeatherAlert {...this.props}/>;
    }

    return <WeatherAlertFromFeed {...this.props}/>;
  }
}

class WeatherAlertFromFeed extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    FRN_componentInstanceId: PropTypes.string,
    title: PropTypes.string,
    alertsPageUri: PropTypes.string,
    allowedAlerts: PropTypes.array,
    dismissOptions: PropTypes.shape({
      isDismissable: PropTypes.bool,
      dismissTimeInHours: PropTypes.number
    })
  }

  static defaultProps = {
    title: "Weather Advisory In Effect",
    alertsPageUri: '',
    allowedAlerts: [],
    dismissOptions: {
      isDismissable: false,
      dismissTimeInHours: 24
    }
  }

  state = {
    showAlert: true
  }

  componentWillMount() {
    const { FRN_componentInstanceId } = this.props;
    const alertInStore = findAlertInStore(FRN_componentInstanceId);

    if (alertInStore) {
      const { timeout } = alertInStore;
      const time = getDateTimeInSeconds();
      const showAlert = time > timeout;

      if (showAlert) {
        removeAlertFromStore(FRN_componentInstanceId);
      } else {
        this._startAutoShowAlert(timeout - time);
      }

      this.setState({ showAlert });
    }
  }

  _getAllowedAlertsByCategory(category, allowedAlerts) {
    const type = category.toLowerCase();
    return _.find(allowedAlerts, (allowedAlert) => type === allowedAlert.type.toLowerCase());
  }

  _sortAlertByPriority(alerts) {
    const sortedAlerts = [];

    ALERT_CATEGORIES.forEach(ale => {
      alerts.forEach(alert => {
        if (alert.name.toLowerCase() === ale) {
          sortedAlerts.push(alert);
        }
      });
    });

    return sortedAlerts;
  }

  _filterAlertData(alerts, allowedAlerts) {
    const alertData = [];

    alerts.forEach((alert) => {
      const {
        weather: weatherObj,
        weathers: weatherArr,
        name: category
      } = alert;

      // filter allowed alert
      const allowedAlert = _.find(allowedAlerts, (ale) => category.toLowerCase() === ale.type.toLowerCase());

      if (allowedAlert) {
        const { backgroundColor = '#d20202' } = allowedAlert;

        const weathers = weatherArr || (weatherObj ? [weatherObj] : []);
        weathers.forEach((weather) => {
          alertData.push({
            category,
            alertType: weather.name,
            backgroundColor
          });
        });
      }
    });

    return alertData;
  }

  _getAlertTitleToDisplay(alerts) {
    return alerts.slice(0, 3).reduce((result, value, index) => {
      let type = value.alertType;
      if (!result) {
        return type;
      }

      if (alerts.length > 3 && index === 2) {
        type += ',...';
      }

      return result + ', ' + type;
    }, '').toLowerCase();
  }

  _dismissAlert(alerts) {
    const { FRN_componentInstanceId, dismissOptions: { dismissTimeInHours } } = this.props;
    const timeout = convertHoursToSeconds(dismissTimeInHours);

    addAlertToStore(FRN_componentInstanceId, getDateTimeInSeconds() + timeout, alerts);
    this._startAutoShowAlert(5000);

    this.setState({
      showAlert: false
    });
  }

  _startAutoShowAlert(timeout) {
    setTimeout(() => {
      removeAlertFromStore(this.props.FRN_componentInstanceId);

      this.setState({
        showAlert: true
      });
    }, timeout);
  }

  render() {
    const {
      FRN_rawResponses = [],
      alertsPageUri,
      allowedAlerts,
      dismissOptions: {
        isDismissable
      }
    } = this.props;

    const { showAlert } = this.state;

    if (!showAlert) {
      return null;
    }

    const features = _.get(FRN_rawResponses[0], 'data.features', []);
    const weatherAlerts = _.find(features, (f) => f.type === 'weatheralerts');

    const {
      alert: alertObj,
      alerts: alertArr
    } = weatherAlerts || {};

    const alerts = alertArr || (alertObj ? [alertObj] : []);
    const sortedAlerts = this._sortAlertByPriority(alerts);
    const alertData = sortedAlerts.length > 0 ? this._filterAlertData(sortedAlerts, allowedAlerts) : [];

    if (alertData.length === 0) {
      return null;
    }

    let alertTitle = this._getAlertTitleToDisplay(alertData);

    const iconType = getWeatherCategoryFromCondition(alertData[0].alertType);
    const backgroundColor = alertData[0].backgroundColor;
    const isMultipleAlerts = alertData.length > 1;

    return (
      <div className="WeatherAlert">
        { isDismissable && <div className="WeatherAlert-dismissBtn" onClick={() => this._dismissAlert(alertData)}><CancelIcon color="#fff"/></div> }
        <a className="WeatherAlert-stationAlertLink" target="_top" href={alertsPageUri}>
          <div className="WeatherAlert-listAdvisory">
            <div className="WeatherAlert-listItem" style={{backgroundColor}}>
              <WeatherAlertItemFromFeed 
                text={alertTitle}
                iconType={iconType}
                showViewAll={isMultipleAlerts}
              />
            </div>
          </div>
        </a>
        {isMultipleAlerts ?
          <a className="WeatherAlert-totalPillContainer" href={alertsPageUri}>
            <div className="WeatherAlert-totalPill" style={{color: backgroundColor}}>
              <span className="WeatherAlert-totalPillIcon">
                <WeatherAlertIcon iconType={'alert'} />
              </span>
              <span className="WeatherAlert-totalValue">{alertData.length}</span>
            </div>
          </a>
          : null}
      </div>
    );
  }
}

class WeatherAlertItemFromFeed extends Component {
  static propTypes = {
    text: PropTypes.string,
    iconType: PropTypes.string,
    showViewAll: PropTypes.bool
  }

  render() {
    const { text, iconType, showViewAll } = this.props;

    return (
      <div className="WeatherAlertItem">
        <div className="WeatherAlertItem-advisoryImage">
          <WeatherAlertIcon iconType={iconType} />
        </div>
        <div className="WeatherAlertItem-advisoryText">
          <h2 className="WeatherAlertItem-title"><strong>Weather Alert:</strong> {text}</h2>
          {showViewAll && <a className="WeatherAlertItem-viewAll hidden-xs">View all</a>}
        </div>
      </div>
    );
  }
}

/**
 * Class representing the container for the multiday and hourly weather forecast components.
 *
 * Dependent on feed - wsi_endpoint
 */
@connect('Weather')
class WeatherAlerts extends Component {

  static propTypes = {
    FRN_componentInstanceId: PropTypes.string,
    title: PropTypes.string,
    alertsPageUri: PropTypes.string,
    allowedAlerts: PropTypes.array,
    dismissOptions: PropTypes.shape({
      isDismissable: PropTypes.bool,
      dismissTimeInHours: PropTypes.number
    }),
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    WeatherStore: PropTypes.object.isRequired
  }

  static contextTypes = {
    flux: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    title: "Weather Advisory In Effect",
    alertsPageUri: '',
    allowedAlerts: [],
    dismissOptions: {
      isDismissable: false,
      dismissTimeInHours: 24
    }
  }

  state = {
    showModal: false,
    expandedAlertIndex: -1,
    showAllAlerts: false,
    showAlert: true
  }

  componentWillMount() {
    const { flux } = this.context;

    flux.getActions('Weather').readWsi();
  }

  componentWillReceiveProps(nextProps) {
    const {
      WeatherStore: {
        wsi: {
          data: {
            Cities: {
              City: {
                WeatherAlerts: {
                  Alert: newAlerts
                } = {}
              } = {}
            } = {}
          } = {}
        } = {}
      }
    } = nextProps;

    if (newAlerts) {
      const { FRN_componentInstanceId, dismissOptions: { isDismissable } } = this.props;

      if (isDismissable) {
        const alertInStore = findAlertInStore(FRN_componentInstanceId);

        if (alertInStore) {
          const { timeout, data } = alertInStore;

          const time = getDateTimeInSeconds();
          const showAlert = time > timeout || this._isNewData(newAlerts, data);

          if (showAlert) {
            removeAlertFromStore(FRN_componentInstanceId);
          } else {
            this._startAutoShowAlertInterval(timeout - time);
          }

          this.setState({ showAlert });
        }
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this._showAlertAgainInterval);
  }

  _setModalVisible(alertIndex = -1){
    this.setState({
      showModal: true,
      expandedAlertIndex: alertIndex
    });
  }

  // Hide the modal and unassign any expanded alert
  _setModalHidden(){
    this.setState({
      showModal: false,
      expandedAlertIndex: -1
    });
  }

  _toggleAlertListItems(){
    this.setState({
      showAllAlerts: !this.state.showAllAlerts
    });
  }

  // Assign the alert which will display its full text in the modal
  _setExpandedIndex(alertIndex = -1){
    const { expandedAlertIndex } = this.state;
    const newExpandedIndex = alertIndex !== expandedAlertIndex ? alertIndex : -1;

    this.setState({
      expandedAlertIndex: newExpandedIndex
    });
  }

  // When a user clicks on an alert show them either the station's default page, or display
  // a modal with the full alert in it.
  _showFullAlerts(alertIndex = -1, event){
    const { config, location } = this.context;
    const { alertsPageUri } = this.props;
    const { affiliate: { locations: stationLocations = [] } = {} } = config;

    // If the current location isn't a default location in the config, then show the modal with the alert in it,
    // otherwise the default browser behaviour will follow the link given in the prop alertsPageUri
    if (!_.find(stationLocations, { 'zipcode': Number(location.zipcode)}) || !alertsPageUri){
      event.preventDefault();
      this._setModalVisible(alertIndex);
    }
  }

  // Remove any alerts that are not in the filter type, and sort them into the appropriate order. The order is specified
  // by whatever order the allowedAlerts comes in as, and then by date within that list.
  _filterAlertsByType(alerts, allowedAlerts){
    const filteredAlerts = _.filter(alerts,
      (alert) => (_.find(allowedAlerts,
        (filterItem) => this._compareAlertFilter(filterItem, alert)
        )
      )
    );

    const alertWithBackgroundColor = _.map(filteredAlerts,
      (alert) => {
        const alertFilter = _.find(allowedAlerts,
          (filterItem) => this._compareAlertFilter(filterItem, alert)
        );

        return _.assign(alert, {backgroundColor: alertFilter.backgroundColor});
      }
    );

    return _.sortBy(alertWithBackgroundColor,
      (alert) => (_.findIndex(allowedAlerts,
          (filterItem) => this._compareAlertFilter(filterItem, alert)
        )
      )
    );
  }

  _compareAlertFilter(filterItem, alert) {
    const alertTitle = alert['@Headline'] || '';

    return filterItem.type.toLowerCase() === alertTitle.split(' ').pop().toLowerCase();
  }

  _dismissAlert() {
    const { FRN_componentInstanceId, dismissOptions: { dismissTimeInHours } } = this.props;
    const timeout = convertHoursToSeconds(dismissTimeInHours);

    addAlertToStore(FRN_componentInstanceId, getDateTimeInSeconds() + timeout, this.props.WeatherStore.wsi);
    this._startAutoShowAlertInterval(timeout);

    this.setState({
      showAlert: false
    });
  }

  _startAutoShowAlertInterval(timeout) {
    this._showAlertAgainInterval = setInterval(() => {
      removeAlertFromStore(this.props.FRN_componentInstanceId);
      clearInterval(this._showAlertAgainInterval);

      this.setState({
        showAlert: true
      });
    }, timeout);
  }

  _isNewData(newAlerts, rawResponse) {
    const {
      data: {
        Cities: {
          City: {
            WeatherAlerts: {
              Alert: oldAlerts = []
            } = {}
          } = {}
        } = {}
      } = {}
    } = rawResponse;

    const { '@Id': newId } = (_.isArray(newAlerts) ? newAlerts : [newAlerts])[0] || {};
    const { '@Id': oldId } = (_.isArray(oldAlerts) ? oldAlerts : [oldAlerts])[0] || {};

    return oldId !== newId;
  }

  render() {
    const { showModal, expandedAlertIndex, showAllAlerts, showAlert } = this.state;

    if (!showAlert) {
      return null;
    }

    const {
      WeatherStore: {
        wsi: {
          data: wsiData = {}
        } = {}
      },
      title,
      allowedAlerts = [],
      alertsPageUri,
      dismissOptions: { isDismissable },
      timestampOptions = {}
    } = this.props;
    const {
      Cities: {
        City: {
          WeatherAlerts: {
            Alert = []
          } = {}
        } = {}
      } = {}
    } = wsiData;
    const alerts = _.isArray(Alert) ? Alert : [Alert];

    let alertsToDisplay;
    if (allowedAlerts.length > 0) {
      alertsToDisplay = this._filterAlertsByType(alerts, allowedAlerts);
    } else {
      alertsToDisplay = alerts;
    }

    if (alertsToDisplay.length === 0) {
      return null;
    }

    const items = alertsToDisplay.map((alert, i) =>
      <div className="WeatherAlert-listItem" key={i}
           style={{backgroundColor: alert.backgroundColor || '#d20202'}}>
        <WeatherAlertItem
          alert={alert}
          onClick={this._showFullAlerts.bind(this, i)}/>
      </div>);

    return (
      <div className="WeatherAlert">
        { isDismissable && <div className="WeatherAlert-dismissBtn" onClick={() => this._dismissAlert()}><CancelIcon color="#fff"/></div> }
        <a className="WeatherAlert-stationAlertLink" href={alertsPageUri} target="_top">
          <div className="WeatherAlert-listAdvisory">
            {showAllAlerts ? items : items[0]}
          </div>
        </a>
        {alertsToDisplay.length > 1 ?
          <div className="WeatherAlert-totalPillContainer" onClick={() => this._toggleAlertListItems()}>
            <div className="WeatherAlert-totalPill" style={{color: alertsToDisplay[0].backgroundColor || '#d20202'}}>
              <span className="WeatherAlert-totalPillIcon">
                <WeatherAlertIcon iconType={'alert'} />
              </span>
              <span className="WeatherAlert-totalValue">{alertsToDisplay.length}</span>
            </div>
          </div>
          : null}
        <WeatherAlertModal title={title}
                           showModal={showModal}
                           alerts={alertsToDisplay}
                           expandedAlertIndex={expandedAlertIndex}
                           onHideModal={this._setModalHidden.bind(this)}
                           onAlertClick={this._setExpandedIndex.bind(this)}
                           timestampOptions={timestampOptions}/>
      </div>
    );
  }
}

/**
 * Class representing a weather alert item.
 */
class WeatherAlertItem extends Component {
  static propTypes = {
    alert: PropTypes.object.isRequired,
    onClick: PropTypes.func
  }

  _getTimeDuration(startTime, endTime){
    let duration = '';

    if (startTime){
      duration = ' From ' + formatDate(formatWsiDate(startTime), 'dddd, MMMM Do Y, h:mm a z');
    }

    if (endTime){
      duration += ' until ' + formatDate(formatWsiDate(endTime), 'dddd, MMMM Do Y, h:mm a z');
    }

    return duration;
  }

  render() {
    const {
      alert: {
        '@Headline': title,
        '@StartTime': startTime,
        '@EndTime': endTime
      } = {},
      onClick
    } = this.props;
    const category = getWeatherCategoryFromCondition(title);

    return (
      <div className="WeatherAlertItem" onClick={onClick} >
        <div className="WeatherAlertItem-advisoryImage">
          <WeatherAlertIcon iconType={category} />
        </div>
        <div className="WeatherAlertItem-advisoryText">
          <h2 className="WeatherAlertItem-advisoryTitle">{title}</h2>
          <span className="WeatherAlertItem-advisoryDuration">
            {this._getTimeDuration(startTime, endTime)}
          </span>
        </div>
      </div>
    );
  }
}

/**
 * Class representing the modal for the current weather alerts
 */
class WeatherAlertModal extends Component {
  static propTypes = {
    title: PropTypes.string,
    alerts: PropTypes.array.isRequired,
    expandedAlertIndex: PropTypes.number,
    onAlertClick: PropTypes.func,
    showModal: PropTypes.bool.isRequired,
    onHideModal: PropTypes.func.isRequired,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    titleType: PropTypes.string
  }

  render() {
    const {
      title = '',
      alerts = [],
      expandedAlertIndex = -1,
      onAlertClick,
      showModal,
      onHideModal,
      timestampOptions = {},
      titleType = TITLE_TYPE.MODULE_TITLE
    } = this.props;

    const weatherAlertItems = alerts.map((alert, i) =>
      <WeatherAlertModalItem key={i} index={i} alert={alert} expanded={i === expandedAlertIndex} onClick={onAlertClick} timestampOptions={timestampOptions}/>
    );

    return (
      <Modal className="WeatherAlertModal"
             show={showModal}
             onHide={onHideModal}
             backdrop
             aria-labelledby="contained-modal-title-lg">
        <Modal.Header className="WeatherAlertModal-header" closeButton />
        <Modal.Body>
          { title ? <ComponentTitle titleType={titleType} title={title} classes="WeatherAlertModal-title" /> : null }
          {weatherAlertItems}
        </Modal.Body>
      </Modal>
    );
  }
}

/**
 * Class representing a weather alert item in the modal list
 */
class WeatherAlertModalItem extends Component {
  static propTypes = {
    alert: PropTypes.object.isRequired,
    expanded: PropTypes.bool,
    index: PropTypes.number.isRequired,
    onClick: PropTypes.func,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    })
  }

  _onClick() {
    const { onClick, index } = this.props;

    if (onClick) {
      onClick(index);
    }
  }

  render() {
    const {
      alert: {
        '@Headline': title,
        '@Bulletin': bulletin,
        '@StartTime': publishDate,
        backgroundColor = '#d20202'
      } = {},
      expanded = false,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {}
    } = this.props;
    const formattedBulletin = bulletin.toLowerCase().replace(/(\/.*(\d{6}t\d{4}z(\.|-)?){1,3}.*\/\n)|((\D{0,3}\d{3,}(-|>))+(\d{3,6})?\n)|(&&(.*\n)+\$\$)/g, '');
    const formattedDate = formatWsiDate(publishDate);
    const category = getWeatherCategoryFromCondition(title);

    return (
      <div className="WeatherAlertModalItem">
        <div className="WeatherAlertModalItem-advisory">
          <WeatherAlertIcon iconType={category} backgroundColor={backgroundColor} />
          <div className="WeatherAlertModalItem-header" onClick={() => this._onClick()}>
            <h2 className="WeatherAlertModalItem-advisoryTitle">{title}</h2>
            <Timestamp publishDate={formattedDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
          </div>
          {expanded ? <div className="WeatherAlertModalItem-advisoryText"
                           dangerouslySetInnerHTML={{__html: formattedBulletin.replace(/\n/g, '<br />') }} />
            : null}
        </div>
      </div>
    );
  }
}

export default WeatherAlertWrapper;



// WEBPACK FOOTER //
// ./app/components/weather/WeatherAlerts.js